import clsx from "clsx";
import React from "react";
import type NotificationSystem from "react-notification-system";
import { match } from "ts-pattern";

import Notification from "../notification";
import { PageFooter } from "../page_footer";
import { PageHeader } from "../page_header";
import { PatientMenu } from "../patient_menu";

export function Layout({
  children,
  notificationStyle,
  contentClassName,
  displayPatientMenu = true,
  centerFooter = false,
  size,
  chrome = true,
}: {
  children: React.ReactNode;
  notificationStyle?: NotificationSystem.Style;
  contentClassName?: string;
  displayPatientMenu?: boolean;
  centerFooter?: boolean;
  size?: "md-8";
  /**
   * Legacy page chrome (header + patient-menu sidebar + footer). Default `true` — the standard
   * legacy page. Set `false` to render only the content wrapper + notifications, so the page body
   * can be embedded inside DIFFERENT chrome (the redesign-v1 `RedesignAppShell`) without the legacy
   * header/sidebar showing through. Purely presentational — no field or behavior change.
   */
  chrome?: boolean;
}) {
  const sized = match(size)
    .with("md-8", () => (
      <div className="row">
        <div className="col-md-8">{children}</div>
      </div>
    ))
    .with(undefined, () => children)
    .exhaustive();

  // Chromeless: no legacy header/sidebar/footer AND none of the Bootstrap page-shell wrappers
  // (`container` / `page-container` / `page-content-wrapper`) — those assume the legacy sidebar
  // (fixed width + left margin) and would offset/clip the form inside the redesign shell. Render the
  // form body directly; the redesign `RedesignAppShell` <main> supplies the padding.
  if (!chrome) {
    return (
      <>
        <Notification style={notificationStyle} />
        <div className={clsx("page-content", contentClassName)}>{sized}</div>
      </>
    );
  }

  return (
    <>
      <PageHeader />
      <div className="container max-md:tw-px-1">
        <div className="page-container">
          {displayPatientMenu ? <PatientMenu /> : null}
          <Notification style={notificationStyle} />
          <div className="page-content-wrapper">
            <div className={clsx("page-content", contentClassName)} style={{ minHeight: 584 }}>
              {sized}
            </div>
          </div>
        </div>
        <PageFooter center={centerFooter} />
      </div>
    </>
  );
}
